import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "checked", "disabled"];
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { default as withStyles } from '../styles';
import { default as MuiSwitch } from '@mui/material/Switch';
import cn from 'classnames';
import { boxShadowFocus, sizingXLarge, borderWidthThin, borderWidthThick, sizingSmall } from '../styles/tokens';
var styles = function styles(theme) {
  return {
    root: {
      width: sizingXLarge,
      height: '1.25rem',
      padding: 0,
      margin: '0.9375rem 0.5rem 0.75rem 0.75rem',
      borderRadius: '.75rem',
      '&$focused': {
        boxShadow: boxShadowFocus
      }
    },
    switchBase: {
      padding: 0,
      margin: '.125rem',
      '&$checked': {
        color: theme.palette.grey[100],
        opacity: 1,
        '& + $track': {
          backgroundColor: theme.palette.ctaColor.base,
          color: theme.palette.ctaColor.base,
          border: borderWidthThick + " solid " + theme.palette.ctaColor.base,
          opacity: 1
        },
        '& $thumb': {
          border: 'none'
        },
        '&$disabled': {
          color: theme.palette.grey[300],
          '& + $track': {
            backgroundColor: theme.palette.grey[400],
            borderColor: theme.palette.grey[400],
            opacity: 1
          }
        }
      },
      '&$disabled': {
        '& + $track': {
          backgroundColor: theme.palette.grey[400],
          opacity: 1
        },
        '& $thumb': {
          borderColor: theme.palette.grey[300]
        },
        color: theme.palette.grey[300],
        opacity: 1
      }
    },
    track: {
      borderRadius: '.75rem',
      backgroundColor: theme.palette.grey[100],
      opacity: 1,
      border: borderWidthThick + " solid " + theme.palette.grey[400]
    },
    thumb: {
      boxShadow: 'none',
      width: sizingSmall,
      height: sizingSmall,
      border: borderWidthThin + " solid " + theme.palette.grey[400]
    },
    focused: {},
    checked: {},
    disabled: {}
  };
};

/**
 * Use `Switch` to select one of two options, typically a "yes/no" or an "on/off" selection.
 * @done true
 * @updated v7.0.0
 * @versionAdded v0.0.8
 * @examples
 *  SwitchExamples
 *  ControlledExample
 *  SwitchFormGroup
 *  SwitchLabels
 */
var Switch = function Switch(props) {
  var _cn;
  var _useState = useState(false),
    isFocused = _useState[0],
    setIsFocused = _useState[1];
  var classes = props.classes,
    checked = props.checked,
    disabled = props.disabled,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  function handleFocus(e) {
    if (props.onFocus) {
      props.onFocus(e);
    }
    setIsFocused(true);
  }
  function handleBlur(e) {
    if (props.onBlur) {
      props.onBlur(e);
    }
    setIsFocused(false);
  }
  var classOverrides = {
    switchBase: classes.switchBase,
    track: classes.track,
    root: cn(classes.root, (_cn = {}, _cn[classes.focused] = isFocused, _cn)),
    disabled: classes.disabled,
    thumb: classes.thumb,
    checked: classes.checked
  };
  return /*#__PURE__*/React.createElement(MuiSwitch, _extends({
    onFocus: function onFocus(e) {
      return handleFocus(e);
    },
    onBlur: function onBlur(e) {
      return handleBlur(e);
    },
    color: "secondary",
    disableRipple: true,
    classes: classOverrides,
    checked: checked,
    disabled: disabled
  }, rest));
};
Switch.muiName = 'Switch';
Switch.displayName = 'Switch';
Switch.propTypes = {
  /**
   * If `true`, the component is checked.
   */
  checked: PropTypes.bool,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * The default checked state. Use when the component is not controlled.
   */
  defaultChecked: PropTypes.bool,
  /**
   * If `true`, the switch will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * The id of the input element.
   */
  id: PropTypes.string,
  /**
   * Attributes applied to the `input` element.
   */
  inputProps: PropTypes.object,
  /**
   * Use this property to pass a ref callback to the native input component.
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * Callback fired when the state is changed.
   *
   * You can pull out the new value by accessing `event.target.value` (string).
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onBlur: PropTypes.func,
  /**
   * @ignore
   */
  onFocus: PropTypes.func,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * The value of the component. The DOM API casts this to a string.
   * The browser uses "on" as the default value.
   */
  value: PropTypes.any
};
export default withStyles(styles)(Switch);